import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { EVENT_TYPE_CONFIG } from '@/pages/dashboard/shared-data';
import type { CalendarEvent } from '@/mocks/dashboard';

type CondoOption = { id: string; nome: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  prefilledDate: string;
  condoList: CondoOption[];
  onCreated: () => void;
};

const EVENT_TYPES: CalendarEvent['type'][] = [
  'manutencao',
  'vistoria',
  'mudanca',
  'alvara',
  'reuniao',
  'outros',
];

export default function NewEventModal({
  isOpen,
  onClose,
  prefilledDate,
  condoList,
  onCreated,
}: Props) {
  const [titulo, setTitulo] = useState('');
  const [condominioId, setCondominioId] = useState('');
  const [tipo, setTipo] = useState<CalendarEvent['type']>('outros');
  const [hora, setHora] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!titulo.trim()) {
      setError('Informe o título do compromisso.');
      return;
    }

    setSaving(true);
    const payload: any = {
      titulo: titulo.trim(),
      data: prefilledDate,
      tipo,
    };
    if (condominioId) payload.condominio_id = condominioId;
    if (hora) payload.hora = hora;

    const { error: insertError } = await supabase.from('eventos').insert(payload);

    if (insertError) {
      setError(insertError.message || 'Erro ao salvar o evento.');
      setSaving(false);
      return;
    }

    resetForm();
    setSaving(false);
    onCreated();
    onClose();
  };

  const resetForm = () => {
    setTitulo('');
    setCondominioId('');
    setTipo('outros');
    setHora('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatDisplayDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    const months = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];
    return `${d} de ${months[m - 1]} de ${y}`;
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={handleClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-background-50 rounded-xl border border-background-200/70 w-full max-w-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-background-200/70">
            <div>
              <h3 className="text-lg font-semibold text-foreground-900">Novo compromisso</h3>
              <p className="text-sm text-foreground-500 mt-0.5">
                {formatDisplayDate(prefilledDate)}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            {error && (
              <div className="px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Manutenção preventiva - Elevador"
                maxLength={200}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
              />
            </div>

            {/* Condomínio */}
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Condomínio
              </label>
              <select
                value={condominioId}
                onChange={(e) => setCondominioId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors appearance-none cursor-pointer"
              >
                <option value="">Selecione um condomínio</option>
                {condoList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo</label>
              <div className="grid grid-cols-3 gap-2">
                {EVENT_TYPES.map((t) => {
                  const cfg = EVENT_TYPE_CONFIG[t];
                  const selected = tipo === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTipo(t)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                        selected
                          ? `${cfg.badgeColor} border-current/30`
                          : 'border-background-300/60 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${cfg.icon} text-sm`}></i>
                      <span>{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Hora */}
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Horário <span className="text-foreground-400 font-normal">(opcional)</span>
              </label>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors cursor-pointer"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2.5 rounded-lg border border-background-300/60 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-add-line text-sm"></i>
                    <span>Adicionar</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
